import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatSaleItemQtyOrNull } from '@/lib/saleItemQuantity';
import { DeliveryWithDetails } from '@/types/delivery';
import { 
  GripVertical, 
  MapPin, 
  Navigation, 
  CheckCircle, 
  Phone,
  User,
  Package,
  Store,
  Truck,
  ArrowDownToLine
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface SortableDeliveryCardProps {
  delivery: DeliveryWithDetails;
  index: number;
  onNavigate: (delivery: DeliveryWithDetails) => void;
  onComplete: (delivery: DeliveryWithDetails) => void;
}

export const SortableDeliveryCard = ({
  delivery,
  index,
  onNavigate,
  onComplete,
}: SortableDeliveryCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: delivery.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500 text-white';
      case 'high':
        return 'bg-orange-500 text-white';
      case 'medium':
        return 'bg-yellow-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const handleNavigate = () => {
    const address = encodeURIComponent(delivery.delivery_address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
    onNavigate(delivery);
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className={`transition-shadow ${isDragging ? 'shadow-lg ring-2 ring-primary' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Drag Handle */}
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing touch-none p-2 -m-2 rounded-lg hover:bg-muted"
            >
              <GripVertical className="h-6 w-6 text-muted-foreground" />
            </div>

            {/* Order Number Badge */}
            <div className="flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-bold text-primary">#{index + 1}</span>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-base truncate">
                    {delivery.sale?.customer?.name || delivery.contact_name || 'Cliente'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {delivery.delivery_number}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className={getPriorityColor(delivery.priority)}>
                    {delivery.priority === 'urgent' ? 'Urgente' :
                     delivery.priority === 'high' ? 'Alta' :
                     delivery.priority === 'medium' ? 'Média' : 'Baixa'}
                  </Badge>
                  {delivery.type === 'collection' && (
                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 text-xs">
                      <ArrowDownToLine className="h-3 w-3 mr-1" /> Coleta
                    </Badge>
                  )}
                  {delivery.delivery_method === 'pickup' && (
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                      <Store className="h-3 w-3 mr-1" /> Retirada
                    </Badge>
                  )}
                  {delivery.delivery_method === 'shipping' && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                      <Truck className="h-3 w-3 mr-1" /> {delivery.shipping_carrier || 'Transportadora'}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span className="line-clamp-2">{delivery.delivery_address}</span>
              </div>

              {delivery.contact_phone && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <a href={`tel:${delivery.contact_phone}`} className="hover:text-primary">
                    {delivery.contact_phone}
                  </a>
                </div>
              )}

              {delivery.scheduled_time && (
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">
                    Agendado: {delivery.scheduled_time}
                  </Badge>
                </div>
              )}

              {delivery.items && delivery.items.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Package className="h-4 w-4" />
                    <span>{delivery.items.length} item(ns)</span>
                  </div>
                  <div className="text-sm pl-6 space-y-0.5">
                    {delivery.items.slice(0, 3).map((item, idx) => (
                      <p key={idx} className="truncate text-muted-foreground">
                        • {item.product_name}{' '}
                        {(() => {
                          const qty = formatSaleItemQtyOrNull(item.quantity, item.sale_item);
                          return qty ? `— ${qty}` : `(${item.quantity}x)`;
                        })()}
                      </p>
                    ))}
                    {delivery.items.length > 3 && (
                      <p className="text-xs text-muted-foreground/70">
                        + {delivery.items.length - 3} mais itens...
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4 pt-3 border-t">
            <Button
              variant="outline"
              className="flex-1 h-12 gap-2 text-base"
              onClick={handleNavigate}
            >
              <Navigation className="h-5 w-5" />
              Navegar
            </Button>
            <Button
              className={`flex-1 h-12 gap-2 text-base ${
                delivery.type === 'collection' 
                  ? 'bg-orange-600 hover:bg-orange-700' 
                  : 'bg-green-600 hover:bg-green-700'
              }`}
              onClick={() => onComplete(delivery)}
            >
              <CheckCircle className="h-5 w-5" />
              {delivery.type === 'collection' ? 'Coletar' : 'Entregar'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
